import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, XCircle } from "lucide-react";
import api, { formatApiError } from "../../../lib/api";
import { formatDateTime } from "../../../lib/datetime";

/**
 * PDF / image preview modal with pinned reviewer notes + drag-to-mark
 * regions on images. Notes CRUD hits /admin/documents/{doc_id}/notes.
 *
 * Props: preview = {url, filename, type, docId}, onClose = () => void
 */
export default function PdfPreviewModal({ preview, onClose }) {
  const isPdf = (preview.type || "").includes("pdf") || (preview.filename || "").toLowerCase().endsWith(".pdf");
  const isImage = (preview.type || "").startsWith("image/");
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [tag, setTag] = useState("note");
  const [page, setPage] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingBox, setPendingBox] = useState(null); // {x,y,w,h} 0-1 while drawing
  const [drawing, setDrawing] = useState(null); // {startX,startY} during drag
  const overlayRef = React.useRef(null);

  const loadNotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/documents/${preview.docId}/notes`);
      setNotes(data?.notes || []);
    } catch { /* non-fatal */ }
  }, [preview.docId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      const payload = { note: body, tag };
      if (page) payload.page = Number(page) || undefined;
      if (pendingBox) payload.bbox = pendingBox;
      const { data } = await api.post(`/admin/documents/${preview.docId}/notes`, payload);
      setNotes((n) => [data.note, ...n]);
      setDraft("");
      setPendingBox(null);
      toast.success(pendingBox ? "Note pinned with region." : "Note pinned to document.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const resolveNote = async (noteId) => {
    try {
      await api.post(`/admin/documents/${preview.docId}/notes/${noteId}/resolve`);
      setNotes((n) => n.map((x) => (x.note_id === noteId ? { ...x, resolved: true } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note? (audit trail keeps it)")) return;
    try {
      await api.delete(`/admin/documents/${preview.docId}/notes/${noteId}`);
      setNotes((n) => n.filter((x) => x.note_id !== noteId));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-ink-bg/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="kyc-doc-preview-modal"
    >
      <div
        className="relative w-full max-w-6xl h-[88vh] bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-navy/10 bg-navy/[0.03]">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Document Preview</div>
            <div className="font-heading text-ink truncate" data-testid="kyc-doc-preview-title">{preview.filename}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={preview.url}
              download={preview.filename}
              className="px-3 py-1.5 rounded border border-navy/15 text-xs font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
              data-testid="kyc-doc-preview-download"
            >
              <Download size={12} /> Download
            </a>
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded bg-navy text-white text-xs font-heading hover:bg-[#256E58] inline-flex items-center gap-1"
              data-testid="kyc-doc-preview-close"
              aria-label="Close preview"
            >
              <XCircle size={13} /> Close
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[1fr_320px]">
          <div className="min-h-0 bg-navy/[0.02]">
            {isPdf || preview.type === "application/octet-stream" ? (
              <iframe
                src={preview.url}
                title={preview.filename}
                className="w-full h-full"
                data-testid="kyc-doc-preview-iframe"
              />
            ) : isImage ? (
              <div
                className="w-full h-full flex items-center justify-center p-4 relative select-none"
                data-testid="kyc-doc-preview-image-wrap"
              >
                <div
                  ref={overlayRef}
                  className="relative inline-block max-w-full max-h-full"
                  onMouseDown={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width;
                    const y = (e.clientY - rect.top) / rect.height;
                    setDrawing({ x, y });
                    setPendingBox({ x, y, w: 0, h: 0 });
                  }}
                  onMouseMove={(e) => {
                    if (!drawing) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const cx = (e.clientX - rect.left) / rect.width;
                    const cy = (e.clientY - rect.top) / rect.height;
                    setPendingBox({
                      x: Math.max(0, Math.min(drawing.x, cx)),
                      y: Math.max(0, Math.min(drawing.y, cy)),
                      w: Math.min(1, Math.abs(cx - drawing.x)),
                      h: Math.min(1, Math.abs(cy - drawing.y)),
                    });
                  }}
                  onMouseUp={() => {
                    setDrawing(null);
                    if (pendingBox && (pendingBox.w < 0.02 || pendingBox.h < 0.02)) setPendingBox(null);
                  }}
                  onMouseLeave={() => setDrawing(null)}
                >
                  <img
                    src={preview.url}
                    alt={preview.filename}
                    className="block max-w-full max-h-[70vh] object-contain cursor-crosshair"
                    draggable={false}
                    data-testid="kyc-doc-preview-image"
                  />
                  {/* Existing region highlights */}
                  {notes.filter((n) => n.bbox && !n.resolved).map((n) => (
                    <div
                      key={n.note_id}
                      className={`absolute border-2 ${
                        n.tag === "concern"
                          ? "border-rose-500 bg-rose-500/20"
                          : n.tag === "confirm"
                            ? "border-emerald-500 bg-emerald-500/20"
                            : "border-gold bg-gold/20"
                      } pointer-events-none`}
                      style={{
                        left: `${(n.bbox.x || 0) * 100}%`,
                        top: `${(n.bbox.y || 0) * 100}%`,
                        width: `${(n.bbox.w || 0) * 100}%`,
                        height: `${(n.bbox.h || 0) * 100}%`,
                      }}
                      title={n.note}
                      data-testid={`kyc-doc-bbox-${n.note_id}`}
                    />
                  ))}
                  {/* Drag-in-progress */}
                  {pendingBox && (
                    <div
                      className="absolute border-2 border-dashed border-[#02BB8E] bg-[#02BB8E]/20 pointer-events-none"
                      style={{
                        left: `${pendingBox.x * 100}%`,
                        top: `${pendingBox.y * 100}%`,
                        width: `${pendingBox.w * 100}%`,
                        height: `${pendingBox.h * 100}%`,
                      }}
                      data-testid="kyc-doc-bbox-pending"
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center">
                <FileText size={40} className="text-ink-muted mb-3" />
                <div className="text-sm text-ink-soft mb-3">
                  Preview isn&apos;t supported for this file type ({preview.type || "unknown"}).
                </div>
                <a
                  href={preview.url}
                  download={preview.filename}
                  className="px-4 py-2 bg-navy text-white text-xs font-heading rounded inline-flex items-center gap-1 hover:bg-[#256E58]"
                >
                  <Download size={13} /> Download to view
                </a>
              </div>
            )}
          </div>
          {/* Notes / annotations panel */}
          <div className="border-l border-navy/10 bg-white flex flex-col min-h-0" data-testid="kyc-doc-notes-panel">
            <div className="px-4 py-3 border-b border-navy/10">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Reviewer Notes</div>
              <div className="text-xs text-ink-soft mt-0.5">Pin observations. Audited. Founder can&apos;t see these.</div>
            </div>
            <div className="p-3 border-b border-navy/10 space-y-2">
              <select
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className="w-full text-xs bg-white border border-navy/15 rounded px-2 py-1 text-ink focus:border-gold outline-none"
                data-testid="kyc-doc-note-tag"
              >
                <option value="note">Note</option>
                <option value="concern">Concern</option>
                <option value="confirm">Confirm</option>
                <option value="followup">Follow-up</option>
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  placeholder="Page"
                  value={page}
                  onChange={(e) => setPage(e.target.value)}
                  className="w-20 text-xs bg-white border border-navy/15 rounded px-2 py-1 text-ink focus:border-gold outline-none"
                  data-testid="kyc-doc-note-page"
                />
                {pendingBox ? (
                  <button
                    onClick={() => setPendingBox(null)}
                    className="flex-1 text-[11px] font-heading text-rose-700 hover:underline text-left px-2"
                    data-testid="kyc-doc-note-clear-region"
                  >
                    Region marked · clear
                  </button>
                ) : (
                  <span className="flex-1 text-[11px] text-ink-muted italic">
                    {isImage ? "Drag on image to mark region" : "PDF: use page # + text note"}
                  </span>
                )}
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. Bank statement page 3 — mismatched entity name."
                rows={3}
                className="w-full text-sm bg-white border border-navy/15 rounded p-2 text-ink focus:border-gold outline-none resize-none"
                data-testid="kyc-doc-note-input"
              />
              <button
                onClick={addNote}
                disabled={busy || !draft.trim()}
                className="w-full px-3 py-1.5 rounded bg-navy text-white text-xs font-heading hover:bg-[#256E58] disabled:opacity-40"
                data-testid="kyc-doc-note-submit"
              >
                {busy ? "Pinning…" : pendingBox ? "Pin Note + Region" : "Pin Note"}
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
              {notes.length === 0 && (
                <div className="text-xs text-ink-muted italic" data-testid="kyc-doc-notes-empty">
                  No notes yet — pin the first one to start the audit trail.
                </div>
              )}
              {notes.map((n) => (
                <div
                  key={n.note_id}
                  className={`p-3 rounded border text-xs ${
                    n.resolved
                      ? "border-navy/[0.06] bg-navy/[0.02] text-ink-muted line-through"
                      : n.tag === "concern"
                        ? "border-rose-200 bg-rose-50 text-rose-900"
                        : n.tag === "confirm"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border-navy/[0.10] bg-white text-ink"
                  }`}
                  data-testid={`kyc-doc-note-${n.note_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest">
                      {n.tag || "note"}{n.page ? ` · p${n.page}` : ""}{n.bbox ? " · region" : ""}{n.resolved ? " · resolved" : ""}
                    </span>
                    <div className="flex items-center gap-1">
                      {!n.resolved && (
                        <button
                          onClick={() => resolveNote(n.note_id)}
                          className="text-[10px] font-heading text-emerald-700 hover:underline"
                          data-testid={`kyc-doc-note-resolve-${n.note_id}`}
                        >
                          Resolve
                        </button>
                      )}
                      <button
                        onClick={() => deleteNote(n.note_id)}
                        className="text-[10px] font-heading text-rose-700 hover:underline"
                        data-testid={`kyc-doc-note-delete-${n.note_id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="whitespace-pre-wrap">{n.note}</div>
                  <div className="text-[10px] font-mono text-ink-muted mt-1.5">
                    {(n.created_by_email || "reviewer").split("@")[0]} · {n.created_at ? formatDateTime(n.created_at) : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-4 py-2 border-t border-navy/10 text-[11px] text-ink-muted flex items-center justify-between">
          <span>Press <kbd className="px-1 border border-navy/15 rounded">Esc</kbd> to close</span>
          <span className="font-mono">{preview.type || "unknown"}</span>
        </div>
      </div>
    </div>
  );
}
